import { decodeMimeWords, senderLabel } from "@/lib/mime";
import EmailHtmlView from "@/components/EmailHtmlView";
import {
  LogOut, Mail, Shield, X, Menu, LayoutDashboard, Sliders, User, Users,
  ArrowLeft, ExternalLink, Plus, RefreshCw, KeyRound, Paintbrush, Ticket, Paperclip, Download, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

import { AnimatePresence, motion } from "framer-motion";
const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import useAdminPanel from "@/hooks/useAdminPanel";
import EmailInboxView from "@/components/admin/EmailInboxView";
import DashboardContent from "@/components/admin/DashboardContent";
import ConfigContent from "@/components/admin/ConfigContent";
import CustomizationContent from "@/components/admin/CustomizationContent";
import SessionsContent from "@/components/admin/SessionsContent";
import SettingsContent from "@/components/admin/SettingsContent";
import UsersContent from "@/components/admin/UsersContent";
import TicketsContent from "@/components/admin/TicketsContent";
import CronContent from "@/components/admin/CronContent";

function AdminPanel({ onLogout, onSwitchToPublic }) {
  const ap = useAdminPanel({ onLogout });

  const isSuperAdmin = ap.adminRole === "super_admin";
  const perms = ap.adminPermissions || {};

  const allMenuItems = [
    { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { id: "config", label: "Configuration", icon: Sliders, permission: "can_edit_config" },
    { id: "customization", label: "Personnalisation", icon: Paintbrush, permission: "can_customize" },
    { id: "users", label: "Utilisateurs", icon: Users, permission: "can_manage_users" },
    { id: "tickets", label: "Tickets", icon: Ticket },
    { id: "cron", label: "Cron", icon: Clock, permission: "can_edit_config" },
    { id: "sessions", label: "Sessions", icon: KeyRound },
    { id: "settings", label: "Compte", icon: User },
  ];

  const menuItems = allMenuItems.filter(item =>
    !item.permission || isSuperAdmin || perms[item.permission]
  );

  return (
    <div className="min-h-screen bg-admin-gradient flex overflow-x-hidden w-full max-w-full">
      {/* Mobile Sidebar Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${ap.sidebarOpen ? "bg-black/50 pointer-events-auto" : "bg-transparent pointer-events-none"}`}
        onClick={() => ap.setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-zinc-950/95 backdrop-blur-xl border-r border-zinc-800/50
        transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
        ${ap.sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full relative" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          {/* Subtle side glow */}
          <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-amber-500/0 via-amber-500/20 to-amber-500/0" />

          <div className="p-4 border-b border-zinc-800/50 flex items-center justify-between">
            <div className="flex items-center gap-2.5 cursor-pointer group/title" onClick={() => window.location.reload()}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/20 flex items-center justify-center group-hover/title:border-amber-500/40 group-hover/title:shadow-[0_0_12px_rgba(245,158,11,0.15)] transition-colors duration-200">
                <Shield className="w-4.5 h-4.5 text-amber-500 group-hover/title:scale-110 transition-transform duration-300" />
              </div>
              <span className="font-mono font-bold text-white group-hover/title:text-amber-400 transition-colors duration-200">{ap.editAdminTitle}</span>
            </div>
            <button onClick={() => ap.setSidebarOpen(false)} className="lg:hidden text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 border-b border-zinc-800/50">
            <div className="flex items-center gap-3">
              {ap.adminAvatar ? (
                <div className="relative">
                  <img src={ap.adminAvatar} alt="Avatar" className="w-10 h-10 rounded-full object-cover border-2 border-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.1)]" />
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-950" />
                </div>
              ) : (
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-500" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-zinc-950" />
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-white">{ap.adminUsername || "Admin"}</p>
                <p className="text-[11px] text-zinc-500 font-medium">{isSuperAdmin ? "Super Admin" : "Administrateur"}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-3 space-y-0.5">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  ap.setActiveTab(item.id);
                  ap.setSidebarOpen(false);
                  ap.setSelectedEmail(null);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                  transition-colors duration-150 text-sm font-medium group/nav relative
                  ${ap.activeTab === item.id && !ap.selectedEmail
                    ? 'bg-amber-500/15 text-amber-400 shadow-[inset_0_0_12px_rgba(245,158,11,0.06)]'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50 hover:translate-x-0.5'
                  }
                `}
              >
                {ap.activeTab === item.id && !ap.selectedEmail && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]" />
                )}
                <item.icon className="w-5 h-5 transition-transform duration-300 group-hover/nav:scale-110 group-hover/nav:rotate-6" />
                {item.label}
              </button>
            ))}

            <div className="my-3 border-t border-zinc-800/50" />

            <button
              onClick={() => { ap.setSidebarOpen(false); onSwitchToPublic(ap.selectedEmail); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors duration-150 text-sm font-medium group/mail"
              data-testid="switch-to-public-btn"
            >
              <Mail className="w-5 h-5 transition-transform duration-300 group-hover/mail:scale-110 group-hover/mail:rotate-6" />
              Boîte mail
              <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-40" />
            </button>
          </nav>

          <div className="p-3 border-t border-zinc-800/50">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-150 text-sm font-medium group/logout"
            >
              <LogOut className="w-5 h-5 transition-transform duration-300 group-hover/logout:scale-110 group-hover/logout:-rotate-12" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="border-b border-zinc-800/50 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-30 relative" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
          <div className="px-4 sm:px-6 py-3 sm:py-3.5 flex items-center justify-between min-h-[56px]">
            <div className="flex items-center gap-3">
              <button onClick={() => ap.setSidebarOpen(true)} className="lg:hidden text-zinc-400 hover:text-white p-1">
                <Menu className="w-6 h-6" />
              </button>
              {ap.selectedEmail ? (
                <div className="flex items-center gap-2">
                  <button onClick={ap.closeEmailInbox} className="text-zinc-400 hover:text-white p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div>
                    <h1 className="font-mono text-sm sm:text-lg font-bold text-white truncate max-w-[200px] sm:max-w-none">
                      {ap.selectedEmail.full_address}
                    </h1>
                    <p className="text-[11px] text-zinc-500 hidden sm:block">Boîte de réception</p>
                  </div>
                </div>
              ) : (
                <h1 className="font-mono text-base sm:text-lg font-bold text-white tracking-tight">
                  {ap.activeTab === "dashboard" ? "Tableau de bord" : ap.activeTab === "config" ? "Configuration" : ap.activeTab === "customization" ? "Personnalisation" : ap.activeTab === "users" ? "Utilisateurs" : ap.activeTab === "tickets" ? "Tickets" : ap.activeTab === "cron" ? "Tâches planifiées" : ap.activeTab === "sessions" ? "Sessions" : "Compte"}
                </h1>
              )}
            </div>
            <Badge className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 text-amber-400 border-amber-500/25 text-[10px] font-mono tracking-widest px-3 py-1 shadow-[0_0_10px_rgba(245,158,11,0.08)]">
              ADMIN
            </Badge>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={ap.selectedEmail ? `email-${ap.selectedEmail.id}` : ap.activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              {ap.selectedEmail ? (
                <EmailInboxView
                  email={ap.selectedEmail}
                  messages={ap.emailMessages}
                  isLoading={ap.isLoadingMessages}
                  onRefresh={() => ap.fetchEmailMessages(ap.selectedEmail.id)}
                  onMessageClick={ap.fetchMessage}
                  formatTime={ap.formatTime}
                  copyToClipboard={ap.copyToClipboard}
                />
              ) : ap.activeTab === "dashboard" ? (
                <DashboardContent
                  stats={ap.stats}
                  emails={ap.emails}
                  isLoading={ap.isLoading}
                  expandedEmail={ap.expandedEmail}
                  toggleEmailExpand={ap.toggleEmailExpand}
                  fetchStats={ap.fetchStats}
                  fetchEmails={ap.fetchEmails}
                  deleteEmail={ap.deleteEmail}
                  deleteEmailsBulk={ap.deleteEmailsBulk}
                  deleteAllEmails={ap.deleteAllEmails}
                  extendEmail={ap.extendEmail}
                  expireEmail={ap.expireEmail}
                  togglePinEmail={ap.togglePinEmail}
                  toggleReplyEmail={ap.toggleReplyEmail}
                  copyToClipboard={ap.copyToClipboard}
                  formatDate={ap.formatDate}
                  formatRemainingTime={ap.formatRemainingTime}
                  setIsCreateModalOpen={ap.setIsCreateModalOpen}
                  openEmailInbox={ap.openEmailInbox}
                  onSwitchToPublic={onSwitchToPublic}
                  siteSettings={ap.siteSettings}
                  emailSearchQuery={ap.emailSearchQuery}
                  setEmailSearchQuery={ap.setEmailSearchQuery}
                  canDeleteEmails={isSuperAdmin || !!perms.can_delete_emails}
                  users={ap.users}
                  assignEmailToUser={ap.assignEmailToUser}
                  unassignEmailFromUser={ap.unassignEmailFromUser}
                  fetchEmails2={ap.fetchEmails}
                  token={ap.token}
                />
              ) : ap.activeTab === "config" && (isSuperAdmin || perms.can_edit_config) ? (
                <ConfigContent
                  editSiteName={ap.editSiteName}
                  setEditSiteName={ap.setEditSiteName}
                  editDurations={ap.editDurations}
                  editDefaultDuration={ap.editDefaultDuration}
                  setEditDefaultDuration={ap.setEditDefaultDuration}
                  editAllowPermanent={ap.editAllowPermanent}
                  setEditAllowPermanent={ap.setEditAllowPermanent}
                  editAllowCustomAddress={ap.editAllowCustomAddress}
                  setEditAllowCustomAddress={ap.setEditAllowCustomAddress}
                  editMaintenanceMode={ap.editMaintenanceMode}
                  setEditMaintenanceMode={ap.setEditMaintenanceMode}
                  editAdminTitle={ap.editAdminTitle}
                  setEditAdminTitle={ap.setEditAdminTitle}
                  addDuration={ap.addDuration}
                  removeDuration={ap.removeDuration}
                  updateDuration={ap.updateDuration}
                  editDomains={ap.editDomains}
                  addDomain={ap.addDomain}
                  removeDomain={ap.removeDomain}
                  updateDomain={ap.updateDomain}
                  saveSiteSettings={ap.saveSiteSettings}
                  isSavingConfig={ap.isSavingConfig}
                  primaryDomain={ap.siteSettings?.domains?.[0] || "shadowmail.cc"}
                />
              ) : ap.activeTab === "customization" && (isSuperAdmin || perms.can_customize) ? (
                <CustomizationContent
                  editAccentColor={ap.editAccentColor}
                  setEditAccentColor={ap.setEditAccentColor}
                  editWelcomeText={ap.editWelcomeText}
                  setEditWelcomeText={ap.setEditWelcomeText}
                  editFooterText={ap.editFooterText}
                  setEditFooterText={ap.setEditFooterText}
                  saveSiteSettings={ap.saveSiteSettings}
                  isSavingConfig={ap.isSavingConfig}
                />
              ) : ap.activeTab === "users" && (isSuperAdmin || perms.can_manage_users) ? (
                <UsersContent
                  users={ap.users}
                  usersStats={ap.usersStats}
                  isLoadingUsers={ap.isLoadingUsers}
                  fetchUsers={ap.fetchUsers}
                  fetchUsersStats={ap.fetchUsersStats}
                  createUser={ap.createUser}
                  updateUser={ap.updateUser}
                  deleteUser={ap.deleteUser}
                  assignEmailToUser={ap.assignEmailToUser}
                  unassignEmailFromUser={ap.unassignEmailFromUser}
                  searchEmailsForAssign={ap.searchEmailsForAssign}
                  fetchEmailsByIds={ap.fetchEmailsByIds}
                  currentAdminRole={ap.adminRole}
                />
              ) : ap.activeTab === "tickets" ? (
                <TicketsContent token={ap.token} />
              ) : ap.activeTab === "cron" && (isSuperAdmin || perms.can_edit_config) ? (
                <CronContent
                  cronJobs={ap.cronJobs}
                  cronEvents={ap.cronEvents}
                  isLoadingCron={ap.isLoadingCron}
                  runningJobId={ap.runningJobId}
                  fetchCron={ap.fetchCron}
                  updateCronJob={ap.updateCronJob}
                  runCronJob={ap.runCronJob}
                  clearCronEvents={ap.clearCronEvents}
                  createCronJob={ap.createCronJob}
                  deleteCronJob={ap.deleteCronJob}
                  testCronJob={ap.testCronJob}
                  previewCronSchedule={ap.previewCronSchedule}
                />
              ) : ap.activeTab === "sessions" ? (
                <SessionsContent
                  sessions={ap.sessions}
                  isLoadingSessions={ap.isLoadingSessions}
                  fetchSessions={ap.fetchSessions}
                  revokeSession={ap.revokeSession}
                  revokeAllSessions={ap.revokeAllSessions}
                />
              ) : (
                <SettingsContent
                  adminUsername={ap.adminUsername}
                  currentPassword={ap.currentPassword}
                  setCurrentPassword={ap.setCurrentPassword}
                  newUsername={ap.newUsername}
                  setNewUsername={ap.setNewUsername}
                  newPassword={ap.newPassword}
                  setNewPassword={ap.setNewPassword}
                  confirmPassword={ap.confirmPassword}
                  setConfirmPassword={ap.setConfirmPassword}
                  showCurrentPassword={ap.showCurrentPassword}
                  setShowCurrentPassword={ap.setShowCurrentPassword}
                  showNewPassword={ap.showNewPassword}
                  setShowNewPassword={ap.setShowNewPassword}
                  isSaving={ap.isSaving}
                  saveCredentials={ap.saveCredentials}
                  token={ap.token}
                  adminAvatar={ap.adminAvatar}
                  uploadAvatar={ap.uploadAvatar}
                  deleteAvatar={ap.deleteAvatar}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Create Email Modal */}
      <Dialog open={ap.isCreateModalOpen} onOpenChange={ap.setIsCreateModalOpen}>
        <DialogContent className="bg-zinc-950/95 backdrop-blur-xl border border-zinc-800/50 mx-4 sm:mx-auto max-w-md shadow-[0_0_40px_rgba(0,0,0,0.5)]">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
              <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-500/20 flex items-center justify-center">
                <Plus className="w-3.5 h-3.5 text-amber-500" />
              </div>
              Créer un Email
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs sm:text-sm">
              Créez un nouvel email avec une durée personnalisée
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs sm:text-sm text-zinc-400 mb-2 block">
                Adresse personnalisée (optionnel)
              </label>
              <Input
                value={ap.customAddress}
                onChange={(e) => ap.setCustomAddress(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
                placeholder="monaddresse"
                className="bg-zinc-900 border-zinc-700 text-white text-sm"
              />
              <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">
                Laissez vide pour générer automatiquement
              </p>
            </div>

            <div>
              <label className="text-xs sm:text-sm text-zinc-400 mb-2 block">Durée de vie</label>
              <Select value={ap.newEmailLifetime} onValueChange={ap.setNewEmailLifetime}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {ap.siteSettings?.durations?.filter(d => d.enabled !== false).map(d => (
                    <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                  ))}
                  <SelectItem value="permanent" className="text-amber-400">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={ap.createEmail}
              disabled={ap.isCreating}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black text-sm"
            >
              {ap.isCreating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Créer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Detail Modal */}
      <Dialog open={ap.isMessageModalOpen} onOpenChange={ap.setIsMessageModalOpen}>
        <DialogContent className="bg-zinc-950/95 backdrop-blur-xl border border-zinc-800/50 mx-4 sm:mx-auto max-w-[calc(100vw-1rem)] sm:max-w-2xl overflow-hidden [&>*]:min-w-0 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
          <DialogHeader>
            <DialogTitle className="font-mono text-cyan-400 text-sm sm:text-base">
              {decodeMimeWords(ap.selectedMessage?.subject)}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              De: {ap.selectedMessage?.sender}
            </DialogDescription>
          </DialogHeader>

          {ap.selectedMessage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2 py-2 border-b border-zinc-800">
                <div className="min-w-0">
                  <span className="text-zinc-500 text-xs font-mono uppercase">
                    {ap.selectedMessage.direction === "sent" ? "→" : "De:"}
                  </span>
                  <p className={`font-mono text-sm break-all ${ap.selectedMessage.direction === "sent" ? "text-emerald-400" : "text-white"}`}>
                    {ap.selectedMessage.direction === "sent" ? (ap.selectedMessage.recipient || ap.selectedMessage.sender) : senderLabel(ap.selectedMessage)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-zinc-500 text-xs font-mono uppercase">{ap.selectedMessage.direction === "sent" ? "Envoyé:" : "Reçu:"}</span>
                  <p className="text-zinc-400 font-mono text-xs">
                    {new Date(ap.selectedMessage.received_at).toLocaleString("fr-FR")}
                  </p>
                </div>
              </div>

              <ScrollArea className="h-[300px] [&>[data-radix-scroll-area-viewport]>div]:!block">
                {ap.selectedMessage.body_html ? (
                  <EmailHtmlView html={ap.selectedMessage.body_html} />
                ) : (
                  <pre className="whitespace-pre-wrap break-words text-zinc-300 font-mono text-sm">
                    {ap.selectedMessage.body}
                  </pre>
                )}
                {ap.selectedMessage.attachments?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] text-zinc-500 font-mono uppercase flex items-center gap-1">
                        <Paperclip className="w-3 h-3" /> {ap.selectedMessage.attachments.length} pièce{ap.selectedMessage.attachments.length > 1 ? "s" : ""} jointe{ap.selectedMessage.attachments.length > 1 ? "s" : ""}
                      </p>
                      {ap.selectedMessage.attachments.length > 1 && (
                        <button
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/messages/${ap.selectedMessage.id}/attachments/download-all`);
                              if (!res.ok) {
                                for (const att of ap.selectedMessage.attachments) {
                                  try {
                                    const r = await fetch(`${API}/attachments/${att.id}`);
                                    const b = await r.blob();
                                    const u = URL.createObjectURL(b);
                                    const a = document.createElement("a");
                                    a.href = u; a.download = att.filename || "download";
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(u);
                                  } catch { }
                                  await new Promise(r => setTimeout(r, 300));
                                }
                                return;
                              }
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `pieces-jointes.zip`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              URL.revokeObjectURL(url);
                            } catch { }
                          }}
                          className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-400 hover:text-amber-300 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Tout télécharger
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ap.selectedMessage.attachments.map((att) => (
                        <button
                          key={att.id}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/attachments/${att.id}`);
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = att.filename || "download";
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              URL.revokeObjectURL(url);
                            } catch { }
                          }}
                          className="inline-flex items-center gap-1.5 bg-zinc-800/80 border border-zinc-700 hover:border-amber-500/30 hover:bg-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-300 hover:text-amber-400 transition-colors"
                        >
                          <Paperclip className="w-3.5 h-3.5 text-amber-400" />
                          <span className="max-w-[150px] truncate">{att.filename}</span>
                          <span className="text-zinc-600 text-[9px]">{att.size > 1024 * 1024 ? `${(att.size / 1024 / 1024).toFixed(1)}MB` : `${Math.round(att.size / 1024)}KB`}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPanel;
